// Fork a community patch into the user's own private patch.
//
// All the multi-step work (read source / insert copy / record lineage)
// lives in the Worker so the client just exchanges (jwt, sourcePatchId)
// for a forkPatchId. The user's local song list stays in IDB; the
// caller is responsible for fetching the new patch's code via
// getCloudPatchCode() and seeding a new local Song from it.

import { getSupabase } from '../auth/client';
import { forkPatchRequest } from './api';

export type ForkResult = {
  forkPatchId: string;
  /** True when the lineage row could not be written. Caller should
   *  surface a non-blocking warning rather than treating it as an
   *  error (the fork patch itself was created). */
  lineageMissing?: boolean;
};

export async function forkPatch(sourcePatchId: string): Promise<ForkResult> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('auth not configured');
  // Refresh the session before reading the JWT — if the cached token is
  // close to expiring, the worker round-trip can race the auto-refresh
  // and 401 us mid-fork.
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) throw new Error('not signed in');
  return forkPatchRequest({ jwt: session.access_token, patchId: sourcePatchId });
}

/** Read a single patch's code by id — needed by the fork flow to seed
 *  a new local Song. RLS gates visibility, so this is the same query
 *  whether the patch is the user's own (e.g. just-forked) or someone
 *  else's public/unlisted. */
export async function getCloudPatchCode(
  cloudPatchId: string,
): Promise<{ title: string; code: string } | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('patches')
    .select('title, code')
    .eq('id', cloudPatchId)
    .maybeSingle();
  if (error) throw new Error(`patch fetch failed: ${error.message}`);
  if (!data) return null;
  return { title: data.title as string, code: data.code as string };
}
